import { Link } from "react-router-dom";
import {
    ShieldCheck, MonitorSmartphone, LayoutDashboard, ReceiptText,
    LifeBuoy, RefreshCw, KeyRound, Gauge,
} from "lucide-react";
import PublicHeader from "../../components/layout/PublicHeader";
import PublicFooter from "../../components/layout/PublicFooter";

const benefits = [
    { icon: ShieldCheck, title: "Secure downloads", desc: "Protected, authenticated access tied to each purchase. Your installers, only for you." },
    { icon: MonitorSmartphone, title: "Windows & macOS", desc: "Native installers for your DAW — VST3, AU and AAX, on Windows 10/11 and macOS 11+ (Apple Silicon)." },
    { icon: LayoutDashboard, title: "Your dashboard", desc: "Manage every plugin, license and download from a single private place." },
    { icon: ReceiptText, title: "Purchase history", desc: "Receipts, status and details of every order, always available." },
    { icon: KeyRound, title: "Licensing & offline", desc: "Activate your plugins and keep working even without a connection." },
    { icon: RefreshCw, title: "Free updates", desc: "Get notified in your dashboard whenever a new version is ready to download." },
    { icon: LifeBuoy, title: "Direct support", desc: "Open tickets with the AVX Pro team — plus direct WhatsApp for urgent issues." },
    { icon: Gauge, title: "Studio & live ready", desc: "Processing designed for mixing, mastering, broadcast and live sound." },
];

const compatibility = ["VST3", "AU", "AAX", "Windows 10/11", "macOS 11+", "64-bit", "Apple Silicon"];

export default function Benefits() {
    return (
        <div className="min-h-screen flex flex-col" data-testid="benefits-page">
            <PublicHeader />

            {/* Hero */}
            <section className="border-b border-avx-line py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="label-mono mb-4">/ Benefits</div>
                    <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-3xl">
                        Everything you get with <span className="text-avx-blue">AVX Pro</span>
                    </h1>
                    <p className="text-avx-muted mt-5 max-w-2xl text-base lg:text-lg leading-relaxed">
                        More than plugins: a complete ecosystem to buy, activate, manage and keep your tools up to date — built for professional producers and engineers.
                    </p>
                    <div className="flex flex-wrap gap-3 mt-8">
                        <Link to="/plugins" data-testid="benefits-cta-plugins" className="btn-primary px-7 py-3 text-sm font-mono uppercase tracking-[0.2em] rounded-sm">
                            Explore plugins
                        </Link>
                        <Link to="/register" data-testid="benefits-cta-register" className="btn-secondary px-7 py-3 text-sm font-mono uppercase tracking-[0.2em] rounded-sm">
                            Create account
                        </Link>
                    </div>
                </div>
            </section>

            {/* Benefits grid */}
            <section className="py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="benefits-grid">
                        {benefits.map((b, i) => {
                            const Icon = b.icon;
                            return (
                                <div
                                    key={b.title}
                                    className="panel p-6 hover:border-avx-blue transition-colors group"
                                    style={{ animation: `fadeInUp .5s ease both`, animationDelay: `${i * 70}ms` }}
                                    data-testid={`benefit-card-${i}`}
                                >
                                    <div className="w-11 h-11 rounded-sm bg-avx-blue/10 flex items-center justify-center mb-4 group-hover:bg-avx-blue/20 transition-colors">
                                        <Icon className="w-5 h-5 text-avx-blue" />
                                    </div>
                                    <h3 className="font-heading font-bold text-lg mb-2">{b.title}</h3>
                                    <p className="text-sm text-avx-muted leading-relaxed">{b.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Compatibility strip */}
            <section className="border-t border-avx-line bg-avx-surface/40 py-16">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="label-mono mb-5">/ Compatibility</div>
                    <div className="flex flex-wrap gap-3">
                        {compatibility.map((c) => (
                            <span key={c} className="font-mono text-sm border border-avx-line rounded-sm px-4 py-2 text-avx-muted">
                                {c}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="panel-elevated p-10 lg:p-14 text-center">
                        <h2 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Ready to level up your sound?</h2>
                        <p className="text-avx-muted mt-3 max-w-xl mx-auto">Browse the catalog and start building your AVX Pro setup today.</p>
                        <Link to="/plugins" className="btn-primary inline-block mt-7 px-8 py-3 text-sm font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="benefits-cta-bottom">
                            View plugins
                        </Link>
                    </div>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
}
